import { Box } from '@mui/material';
import React from 'react';
import useThemeContext from '../../../themes/hooks/useThemeContext';

interface GradientBackgroundProps {
     children: React.ReactNode;
}

/**
 * @name GradientBackground
 * @description Componente que fornece um fundo gradiente animado sem restrições de altura ou largura para elementos filhos
 * @param {React.ReactNode} children - Elementos filhos do background
 * @returns {JSX.Element} - JSX do componente de fundo
 */
const GradientBackground: React.FC<GradientBackgroundProps> = ({ children }) => {
     const { theme } = useThemeContext();
     const isDark = theme.palette.mode === 'dark';

     // Cores do tema para os blobs (usando o tema customizado)
     const accent1 = theme.palette.accent1?.main || theme.palette.primary.main;
     const accent2 = theme.palette.accent2?.main || theme.palette.secondary.main;
     const accent3 = theme.palette.accent3?.main || theme.palette.tertiary?.main || theme.palette.primary.main;
     const accent4 = theme.palette.accent4?.main || (isDark ? theme.palette.background.default : '#fff');

     // Gradiente linear animado de fundo (cores originais, mais visível)
     const linearGradient = `linear-gradient(
          120deg,
          ${accent1} 0%,
          ${accent2} 40%,
          ${accent3} 80%,
          ${accent4} 100%
     )`;
     // Gradiente radial sutil para suavizar (mantém alpha)
     const accent2Alpha = accent2.replace(/(rgb[a]?)\(([^)]+)\)/, 'rgba($2,0.18)');
     const radialGradient = `radial-gradient(circle at 60% 40%, ${accent2Alpha} 0%, transparent 70%)`;

     return (
          <Box
               sx={{
                    position: 'relative',
                    overflow: 'hidden',
                    display: 'flex',
                    width: '100%',
                    minHeight: '100%',
                    flexGrow: 1,
                    background: `${linearGradient}, ${radialGradient}`,
                    backgroundBlendMode: 'lighten, normal',
                    backgroundSize: '200% 200%',
                    animation: 'gradientDirection 18s ease-in-out infinite alternate',
                    transition: 'background 2.2s cubic-bezier(0.77,0,0.18,1)',
                    '@keyframes gradientDirection': {
                         '0%': { backgroundPosition: '0% 50%' },
                         '50%': { backgroundPosition: '100% 50%' },
                         '100%': { backgroundPosition: '0% 50%' },
                    },
               }}
          >
               {/* Blobs SVG animados com animações mais suaves */}
               <Box
                    sx={{
                         position: 'absolute',
                         top: '60%',
                         left: '-8%',
                         width: '30vw',
                         height: '30vw',
                         maxWidth: '500px', // Limitando tamanho máximo
                         maxHeight: '500px', // Limitando tamanho máximo
                         minWidth: 120,
                         minHeight: 120,
                         zIndex: 0,
                         filter: 'blur(12px)',
                         opacity: 0.22,
                         pointerEvents: 'none',
                         animation: 'blob4move 26s ease-in-out infinite alternate',
                         '@keyframes blob4move': {
                              '0%': { transform: 'scale(1) rotate(0deg) translateY(0px)' },
                              '40%': { transform: 'scale(1.05) rotate(-10deg) translateY(-10px)' }, // Reduzindo a escala
                              '100%': { transform: 'scale(1) rotate(8deg) translateY(10px)' },
                         },
                    }}
               >
                    <svg
                         width='100%'
                         height='100%'
                         viewBox='0 0 400 400'
                         fill='none'
                         xmlns='http://www.w3.org/2000/svg'
                    >
                         <defs>
                              <radialGradient
                                   id='blob4grad'
                                   cx='50%'
                                   cy='50%'
                                   r='70%'
                              >
                                   <stop
                                        offset='0%'
                                        stopColor={accent4}
                                   />
                                   <stop
                                        offset='100%'
                                        stopColor={accent1}
                                   />
                              </radialGradient>
                         </defs>
                         <path
                              d='M 200 60 Q 350 100 320 200 Q 350 320 200 340 Q 80 320 60 200 Q 100 100 200 60 Z'
                              fill='url(#blob4grad)'
                         />
                    </svg>
               </Box>
               {/* Overlay de ruído sutil para textura */}
               <Box
                    sx={{
                         position: 'absolute',
                         inset: 0,
                         width: '100%',
                         height: '100%',
                         zIndex: 2,
                         pointerEvents: 'none',
                         opacity: isDark ? 0.1 : 0.07,
                         backgroundImage:
                              "url(\"data:image/svg+xml,%3Csvg width='100' height='100' viewBox='0 0 100 100' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Crect width='100' height='100' fill='white' fill-opacity='0'/%3E%3Ccircle cx='10' cy='10' r='1.5' fill='gray' fill-opacity='0.18'/%3E%3Ccircle cx='80' cy='30' r='1' fill='gray' fill-opacity='0.13'/%3E%3Ccircle cx='50' cy='80' r='1.2' fill='gray' fill-opacity='0.12'/%3E%3Ccircle cx='70' cy='60' r='1.1' fill='gray' fill-opacity='0.10'/%3E%3C/svg%3E\")",
                         backgroundRepeat: 'repeat',
                         backgroundSize: '120px 120px',
                         mixBlendMode: isDark ? 'soft-light' : 'multiply',
                    }}
               />
               <Box
                    sx={{
                         position: 'absolute',
                         top: '-15%',
                         left: '-10%',
                         width: '60vw',
                         height: '60vw',
                         maxWidth: '800px', // Limitando tamanho máximo
                         maxHeight: '800px', // Limitando tamanho máximo
                         minWidth: 400,
                         minHeight: 400,
                         zIndex: 0,
                         filter: 'blur(16px)',
                         opacity: 0.55,
                         pointerEvents: 'none',
                         animation: 'blob1move 22s ease-in-out infinite alternate',
                         '@keyframes blob1move': {
                              '0%': { transform: 'scale(1) rotate(0deg) translateY(0px)' },
                              '50%': { transform: 'scale(1.08) rotate(12deg) translateY(20px)' }, // Reduzindo a escala
                              '100%': { transform: 'scale(1) rotate(-5deg) translateY(-10px)' },
                         },
                    }}
               >
                    <svg
                         width='100%'
                         height='100%'
                         viewBox='0 0 700 700'
                         fill='none'
                         xmlns='http://www.w3.org/2000/svg'
                    >
                         <defs>
                              <radialGradient
                                   id='blob1grad'
                                   cx='50%'
                                   cy='50%'
                                   r='70%'
                              >
                                   <stop
                                        offset='0%'
                                        stopColor={accent1}
                                   />
                                   <stop
                                        offset='100%'
                                        stopColor={accent2}
                                   />
                              </radialGradient>
                         </defs>
                         <path
                              d='M 350 80 Q 500 100 600 250 Q 700 400 500 600 Q 350 700 200 600 Q 0 400 100 250 Q 200 100 350 80 Z'
                              fill='url(#blob1grad)'
                         />
                    </svg>
               </Box>
               <Box
                    sx={{
                         position: 'absolute',
                         bottom: '-18%',
                         right: '-12%',
                         width: '55vw',
                         height: '55vw',
                         maxWidth: '700px', // Limitando tamanho máximo
                         maxHeight: '700px', // Limitando tamanho máximo
                         minWidth: 320,
                         minHeight: 320,
                         zIndex: 0,
                         filter: 'blur(18px)',
                         opacity: 0.45,
                         pointerEvents: 'none',
                         animation: 'blob2move 28s ease-in-out infinite alternate',
                         '@keyframes blob2move': {
                              '0%': { transform: 'scale(1) rotate(0deg) translateY(0px)' },
                              '40%': { transform: 'scale(1.05) rotate(-8deg) translateY(-15px)' }, // Reduzindo a escala
                              '100%': { transform: 'scale(1) rotate(6deg) translateY(10px)' },
                         },
                    }}
               >
                    <svg
                         width='100%'
                         height='100%'
                         viewBox='0 0 600 600'
                         fill='none'
                         xmlns='http://www.w3.org/2000/svg'
                    >
                         <defs>
                              <radialGradient
                                   id='blob2grad'
                                   cx='50%'
                                   cy='50%'
                                   r='70%'
                              >
                                   <stop
                                        offset='0%'
                                        stopColor={accent3}
                                   />
                                   <stop
                                        offset='100%'
                                        stopColor={accent4}
                                   />
                              </radialGradient>
                         </defs>
                         <path
                              d='M 300 60 Q 500 120 540 300 Q 600 500 300 540 Q 100 500 60 300 Q 100 120 300 60 Z'
                              fill='url(#blob2grad)'
                         />
                    </svg>
               </Box>
               {/* Blob menor, mais "aleatório" */}
               <Box
                    sx={{
                         position: 'absolute',
                         top: '40%',
                         left: '60%',
                         width: '22vw',
                         height: '22vw',
                         minWidth: 120,
                         minHeight: 120,
                         zIndex: 0,
                         filter: 'blur(10px)',
                         opacity: 0.32,
                         pointerEvents: 'none',
                         animation: 'blob3move 18s ease-in-out infinite alternate',
                         '@keyframes blob3move': {
                              '0%': { transform: 'scale(1) rotate(0deg) translateY(0px)' },
                              '60%': { transform: 'scale(1.18) rotate(24deg) translateY(-18px)' },
                              '100%': { transform: 'scale(1) rotate(-12deg) translateY(12px)' },
                         },
                    }}
               >
                    <svg
                         width='100%'
                         height='100%'
                         viewBox='0 0 300 300'
                         fill='none'
                         xmlns='http://www.w3.org/2000/svg'
                    >
                         <defs>
                              <radialGradient
                                   id='blob3grad'
                                   cx='50%'
                                   cy='50%'
                                   r='70%'
                              >
                                   <stop
                                        offset='0%'
                                        stopColor={accent2}
                                   />
                                   <stop
                                        offset='100%'
                                        stopColor={accent3}
                                   />
                              </radialGradient>
                         </defs>
                         <path
                              d='M 150 30 Q 250 60 270 150 Q 300 250 150 270 Q 60 250 30 150 Q 60 60 150 30 Z'
                              fill='url(#blob3grad)'
                         />
                    </svg>
               </Box>
               {/* Conteúdo sem restrições */}
               <Box
                    sx={{
                         position: 'relative',
                         zIndex: 3,
                         width: '100%',
                         display: 'flex',
                         flexGrow: 1,
                         flexDirection: 'column',
                    }}
               >
                    {children}
               </Box>
          </Box>
     );
};

export default GradientBackground;
